import { useEffect, useId, useState } from 'react';
import { agentAvatarRawSrc, type Agent } from '../api';

/**
 * Avatar de um agente. Se houver foto (avatarKey), aponta o <img> direto pro
 * endpoint de stream da API (funciona em produção, onde a URL assinada do MinIO
 * apontaria pro host interno). Se faltar/falhar, cai no SVG gerado por nome.
 */
export function AgentAvatar({ slug, agent, size = 44 }: { slug: string; agent: Agent; size?: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [agent.avatarKey, agent.id]);

  if (agent.avatarKey && !failed) {
    return (
      <img
        className="agent-avatar"
        src={agentAvatarRawSrc(slug, agent.id, agent.avatarKey)}
        alt={agent.name}
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    );
  }
  return <GenAvatar name={agent.name} size={size} />;
}

/** Avatar SVG gerado a partir de um nome (gradiente + iniciais). */
export function GenAvatar({ name, size = 44 }: { name: string; size?: number }) {
  const gid = useId();
  const h = hashHue(name);
  const h1 = h % 360;
  const h2 = (h * 9 + 60) % 360;
  const initials = name
    .replace(/^[_\s]+/, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="agent-avatar" role="img" aria-label={name}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={`hsl(${h1} 68% 58%)`} />
          <stop offset="100%" stopColor={`hsl(${h2} 64% 46%)`} />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="50" fill={`url(#${gid})`} />
      <text
        x="50"
        y="50"
        dy=".35em"
        textAnchor="middle"
        fontSize="40"
        fontWeight="700"
        fill="#fff"
        fontFamily="system-ui, sans-serif"
      >
        {initials}
      </text>
    </svg>
  );
}

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}
